import React, { useCallback, useState, useEffect } from 'react'
import ReactTooltip from 'react-tooltip'
import { Column } from 'react-table'

import API from '../../../../../../services/api'
import Icon from '../../../../../atoms/icon/Icon'
import Clickable from '../../../../../atoms/clickable/Clickable'
import DeleteParticipant from '../../../participants/DeleteParticipant'
import ParticipantImport from '../../../participants/ParticipantImport'
import StickyBar from '../../../../../organisms/sticky-bar/StickyBar'
import InlineGroup from '../../../../../atoms/inlinegroup/InlineGroup'
import IconButton from '../../../../../molecules/iconbutton/IconButton'
import SortableTable from '../../../../../organisms/sortable-table/Sortable-table'
import Text from '../../../../../atoms/text/Text'
import AddParticipantModal from './AddParticipantModal'
import EditParticipantModal from './EditParticipantModal'

interface Props {
  participants: API.ParticipantResponse[]
  refreshEvent: () => void
  data: any
  event: API.EventResponse
}

interface ParticipantState {
  importModalOpen: boolean
  deleteParticipantModalOpen: boolean
  addParticipantModalOpen: boolean
  editParticipantModalOpen: boolean
  participantToDelete: API.ParticipantResponse | null
  selectedParticipant: API.ParticipantResponse | null
}

const STICKY_BAR_HEIGHT = 20

function ParticipantsSection({
  participants,
  refreshEvent,
  data,
  event,
}: Props) {
  const [
    {
      importModalOpen,
      deleteParticipantModalOpen,
      participantToDelete,
      addParticipantModalOpen,
      editParticipantModalOpen,
      selectedParticipant,
    },
    setParticipantModalState,
  ] = useState<ParticipantState>({
    importModalOpen: false,
    deleteParticipantModalOpen: false,
    participantToDelete: null,
    addParticipantModalOpen: false,
    editParticipantModalOpen: false,
    selectedParticipant: null,
  })

  const [selected, setSelected] = useState<API.ParticipantResponse[]>([])
  const { id: eventId } = event

  const selectedRowsCallback = useCallback(
    (selectedRows: API.ParticipantResponse[]) => {
      setSelected(selectedRows)
    },
    []
  )

  const handleExport = () => {
    if (data) API.exportParticipants(data)
  }

  const onParticipantDeleteComplete = useCallback(() => {
    setParticipantModalState((prevState) => ({
      ...prevState,
      deleteParticipantModalOpen: false,
      participantToDelete: null,
    }))
    refreshEvent()
  }, [refreshEvent])

  const onParticipantImportComplete = useCallback(() => {
    setParticipantModalState((prevState) => ({
      ...prevState,
      importModalOpen: false,
    }))
    refreshEvent()
  }, [refreshEvent])

  useEffect(() => {
    ReactTooltip.rebuild()
  })

  const deleteParticipant = useCallback(
    (participant: API.ParticipantResponse) =>
      setParticipantModalState((prevState) => ({
        ...prevState,
        deleteParticipantModalOpen: true,
        participantToDelete: participant,
      })),
    []
  )

  const tableColumns: Column<API.ParticipantResponse>[] = React.useMemo(
    () => [
      {
        Header: 'Team #',
        accessor: 'team',
      },
      {
        Header: 'CEO',
        accessor: (row) => (row.isCeo ? 'Yes' : 'No'),
      },
      {
        Header: 'First Name',
        accessor: 'firstName',
      },
      {
        Header: 'Last Name',
        accessor: 'lastName',
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }: { row: { original: API.ParticipantResponse } }) => (
          <>
            <Clickable
              data-test={`view-${row.original.id}`}
              onClick={(event) => {
                event.stopPropagation()
                setParticipantModalState((prevState) => ({
                  ...prevState,
                  editParticipantModalOpen: true,
                  selectedParticipant: row.original,
                }))
              }}
            >
              <Icon type="search" tip={{ content: 'View participant' }} />
            </Clickable>
            <Clickable
              data-test={`delete-${row.original.id}`}
              onClick={(event) => {
                event.stopPropagation()
                deleteParticipant(row.original)
              }}
            >
              <Icon type="trash" tip={{ content: 'Delete participant' }} />
            </Clickable>
          </>
        ),
      },
    ],
    [deleteParticipant]
  )

  const onRowClick = useCallback(
    (
      event: { stopPropagation: () => void, preventDefault: () => void },
      row: { original: API.ParticipantResponse }
    ) => {
      event.stopPropagation()
      event.preventDefault()
      setParticipantModalState((prevState) => ({
        ...prevState,
        editParticipantModalOpen: true,
        selectedParticipant: row.original,
      }))
    },
    []
  )

  const navigateParticipant = useCallback(
    (step: number) => {
      if (!selectedParticipant) return

      const currentIndex = event.participants.findIndex(
        (p: API.ParticipantResponse) => p.id === selectedParticipant.id
      )
      let nextIndex = currentIndex + step
      if (nextIndex < 0) {
        nextIndex = event.participantsCount - 1
      } else if (nextIndex >= event.participantsCount) {
        nextIndex = 0
      }
      setParticipantModalState((prevState) => ({
        ...prevState,
        editParticipantModalOpen: true,
        selectedParticipant: event.participants[nextIndex],
      }))
    },
    [event.participants, event.participantsCount, selectedParticipant]
  )

  return (
    <>
      <DeleteParticipant
        isOpen={deleteParticipantModalOpen}
        participants={participantToDelete ? [participantToDelete] : selected}
        eventId={eventId}
        onClose={() =>
          setParticipantModalState((prevState) => ({
            ...prevState,
            deleteParticipantModalOpen: false,
            participantToDelete: null,
          }))
        }
        onComplete={onParticipantDeleteComplete}
      />
      {importModalOpen && (
        <ParticipantImport
          isOpen={importModalOpen}
          onClose={() =>
            setParticipantModalState((prevState) => ({
              ...prevState,
              importModalOpen: false,
            }))
          }
          onComplete={onParticipantImportComplete}
          eventId={eventId}
        />
      )}
      {addParticipantModalOpen && <AddParticipantModal
        isOpen={addParticipantModalOpen}
        onClose={() =>
          setParticipantModalState((prevState) => ({
            ...prevState,
            addParticipantModalOpen: false,
          }))
        }
        onComplete={() => {
          setParticipantModalState((prevState) => ({
            ...prevState,
            addParticipantModalOpen: false,
          }))
          refreshEvent()
        }}
        event={event}
      />}
      {selectedParticipant && (
        <EditParticipantModal
          isOpen={editParticipantModalOpen}
          onClose={() =>
            setParticipantModalState((prevState) => ({
              ...prevState,
              editParticipantModalOpen: false,
              selectedParticipant: null,
            }))
          }
          onComplete={(dismissPopup: boolean) => {
            setParticipantModalState((prevState) => ({
              ...prevState,
              editParticipantModalOpen: !dismissPopup,
              selectedParticipant: null,
            }))
            refreshEvent()
          }}
          event={event}
          participant={selectedParticipant}
          onNextParticipant={() => navigateParticipant(1)}
          onPrevParticipant={() => navigateParticipant(-1)}
          deleteParticipant={deleteParticipant}
        />
      )}
      <StickyBar
        spread
        verticalCenter
        height={STICKY_BAR_HEIGHT}
        verticalTop
        style={{ backgroundColor: 'white' }}
      >
        <InlineGroup spaceLeft={2}>
          <IconButton
            data-test="add-participant"
            icon="addUser"
            text="Add Participant"
            onClick={() =>
              setParticipantModalState((prevState) => ({
                ...prevState,
                addParticipantModalOpen: true,
              }))
            }
          />
          <IconButton
            data-test="import-participant"
            icon="import"
            text="Import Participants"
            onClick={() =>
              setParticipantModalState((prevState) => ({
                ...prevState,
                importModalOpen: true,
              }))
            }
          />
          <IconButton
            data-test="export-participant"
            icon="export"
            text="Export Participants"
            onClick={handleExport}
          />
          <IconButton
            danger
            data-test="delete-participants"
            icon="trash"
            text="Delete Selected"
            onClick={(event) => {
              event.stopPropagation()
              setParticipantModalState((prevState) => ({
                ...prevState,
                deleteParticipantModalOpen: true,
              }))
            }}
          />
        </InlineGroup>
      </StickyBar>
      {!data.participants.length && (
        <Text colour="secondaryDark3" size="lg">
          There are no participants yet
        </Text>
      )}
      {!!data.participants.length && (
        <SortableTable
          stickyHeader
          stickyTop={STICKY_BAR_HEIGHT}
          clickable
          columns={tableColumns}
          data={participants}
          selectedRowsCallback={selectedRowsCallback}
          onRowClick={onRowClick}
        />
      )}
    </>
  )
}

export default ParticipantsSection
